import React, { useCallback, useState } from "react";
import "./CreateResource.scss";
import PropTypes from "prop-types";
import FileUploader from "devextreme-react/file-uploader";
import PopUp, { ToolbarItem } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import { LoadPanel } from "devextreme-react/load-panel";
import ScrollView from "devextreme-react/scroll-view";
import { FaTimesCircle } from "react-icons/fa";
import {
  removeZipSVG,
  fileZipIconSVG,
  validationErrorSVG,
  ValidationSuccessSVG,
  addResourceSVG,
} from "../../Assets/SVGs/ResourceSVGs";
import { dropCertificateSVG } from "../../Assets/SVGs/environmentSVGs";
import Notification from "../Notification/Notification";
import { manifestMessages, ApiSuccessMsgs } from "../../Constants/constants";
import { useUser } from "../../contexts/UserContext";

const resContainerElement = {
  class: "resContainerElement",
};

function lowercaseKeys(obj) {
  return Object.keys(obj).reduce((acc, currentKey) => {
    acc[currentKey.toLowerCase()] = obj[currentKey];
    return acc;
  }, {});
}

const createApiForm = (data) => {
  const manifestMetaDataLowercase = lowercaseKeys(data.manifestMetaData);
  const formData = new FormData();
  if (data.isVersion) {
    formData.append("resourceversion.parentresourceid", manifestMetaDataLowercase.id);
  } else {
    formData.append("resource.id", manifestMetaDataLowercase.id);
    formData.append("resource.name", manifestMetaDataLowercase.name);
    formData.append("resource.resourcetypeid", manifestMetaDataLowercase.type);
    formData.append("resource.url", "");
  }

  formData.append(
    "resourceversion.resourceversionid",
    manifestMetaDataLowercase.versionid
  );
  formData.append("resourceversion.versionnumber", manifestMetaDataLowercase.version);
  formData.append(
    "resourceversion.resourcedetail.releasedate",
    manifestMetaDataLowercase.releasedate
  );
  formData.append("resourceversion.resourcedetail.id", "");
  formData.append("resourceversion.resourcedetail.purpose", "");
  formData.append("resourceversion.resourcedetail.releasenote", "");
  formData.append(
    "resourceversion.resourcedetail.description",
    manifestMetaDataLowercase.description
  );
  Object.keys(manifestMetaDataLowercase.attributes).forEach((attrKey) => {
    formData.append(
      `resourceversion.metadata.${attrKey}`,
      manifestMetaDataLowercase.attributes[attrKey]
    );
  });
  formData.append("filename", data.fileName);
  formData.append("filesize", data.fileSize);
  return formData;
};

const CreateResource = ({ refreshResourceGrid }) => {
  const { backend } = useUser();

  const [fileName, setFileName] = useState("");
  const [createResourceData, setCreateResourceData] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [isCreatePopupVisible, setIsCreatePopupVisible] = useState(false);
  const [isCheckingManifestDetails, setIsCheckingManifestDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const resetFileUpload = () => {
    setFileName("");
    setCreateResourceData(null);
    setValidationErrors(null);
  };

  const handleFileUpload = async (e) => {
    const fileData = e[0];
    setFileName(fileData.name);

    try {
      setIsCheckingManifestDetails(true);
      const manifestFormdata = new FormData();
      manifestFormdata.append("file", fileData);

      const result = await backend.callGetManifestDetails(manifestFormdata);

      if (result.succeeded === true) {
        setCreateResourceData(result.data);
        if (result.data.isValidationFailed === true) {
          setValidationErrors(result.data.validationErrors);
        }
      } else {
        Notification(result.message, "error");
        resetFileUpload();
      }
    } catch (error) {
      setErrorMessage(error.response.data.message);
      resetFileUpload();
      console.error(error);
    } finally {
      setIsCheckingManifestDetails(false);
    }
  };

  const handleCancelUpload = useCallback(() => {
    if (createResourceData && !validationErrors) {
      const formdata = new FormData();
      formdata.append("filename", createResourceData.fileName);
      formdata.append("fileid", createResourceData.manifestMetaData.versionId);
      backend
        .callCancelResourceUpdate(formdata)
        .then((result) => {
          if (result.succeeded) {
            Notification("Resource creation cancelled", "success");
          } else {
            Notification(result.message, "error");
          }
        })
        .catch((result) => Notification(result.message, "error"));
    }
    resetFileUpload();
  }, [backend, createResourceData, validationErrors]);

  const createNewResourceAPI = useCallback(() => {
    // eslint-disable-next-line no-unused-expressions
    createResourceData.isVersion === true
      ? backend
          .callCreateResourceVersion(createApiForm(createResourceData))
          .then((res) => {
            if (res.succeeded) {
              Notification(ApiSuccessMsgs.ResCreated, "success");
              setIsCreatePopupVisible(false);
              resetFileUpload();
            } else {
              Notification(res.message || res.Message, "error");
            }
          })
          .finally(() => {
            refreshResourceGrid();
          })
      : backend
          .callCreateResource(createApiForm(createResourceData))
          .then((res) => {
            if (res.succeeded) {
              Notification(ApiSuccessMsgs.ResCreated, "success");
              setIsCreatePopupVisible(false);
              resetFileUpload();
            } else {
              Notification(res.message || res.Message, "error");
            }
          })
          .finally(() => {
            refreshResourceGrid();
          });
  }, [backend, createResourceData, refreshResourceGrid]);

  const togglePopupVisibility = useCallback(() => {
    setIsCreatePopupVisible((prevState) => !prevState);
    setErrorMessage(null);
    if (createResourceData) {
      handleCancelUpload();
    }
  }, [createResourceData, handleCancelUpload]);

  const getCancelOptions = useCallback(
    () => ({
      text: "Cancel",
      onClick: togglePopupVisibility,
    }),
    [togglePopupVisibility]
  );

  const getCreateOptions = useCallback(
    () => ({
      text: "Create",
      onClick: createNewResourceAPI,
    }),
    [createNewResourceAPI]
  );

  const renderManifestValidatedTable = useCallback(
    () => (
      <div>
        <hr className="vertical-line" />
        <div>
          <div
            className={
              !validationErrors ? "resource-table-success" : "resource-table-div"
            }
          >
            <div className="res-class">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span className="table-header">{manifestMessages.Manifest_heading}</span>
                {!validationErrors && (
                  <span className="text-success suc-val-text">
                    <span>{ValidationSuccessSVG}</span>
                    <span className="validation-suc-text">
                      {manifestMessages.Valid_success}
                    </span>
                  </span>
                )}
              </div>
              <hr className="table-hr" />
              <table className="validation-table-class">
                <tbody>
                  {createResourceData.manifestMetaData &&
                    Object.entries(createResourceData.manifestMetaData).map(
                      ([key, val]) => {
                        const lowercaseKey = key.toLowerCase();
                        if (lowercaseKey !== "attributes") {
                          return (
                            <tr key={key}>
                              <th className="res-det-key">{key}:</th>
                              <td className="res-det-value">{val || "N/A"}</td>
                            </tr>
                          );
                        }
                        if (
                          lowercaseKey === "attributes" &&
                          typeof val === "object" &&
                          val !== null
                        ) {
                          return (
                            <React.Fragment key={key}>
                              {Object.entries(val).map(([attrKey, attrValue]) => (
                                <tr key={attrKey}>
                                  <th className="res-det-key">{attrKey}:</th>
                                  <td className="res-det-value">{attrValue || "N/A"}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        }
                        return null;
                      }
                    )}
                </tbody>
              </table>
            </div>
          </div>

          {validationErrors && (
            <div>
              <div className="validations-div">
                <div>
                  <div>
                    <span className="text-danger d-flex justify-content-center validation-failed-span">
                      {" "}
                      <span>{validationErrorSVG}</span> {manifestMessages.Valid_failed}
                    </span>
                  </div>
                  <span className="manifest-error">
                    <span className=" d-flex justify-content-center">
                      {manifestMessages.doesnt_contain}
                    </span>
                    <span className=" d-flex justify-content-center">
                      {" "}
                      {manifestMessages.check}
                    </span>
                  </span>
                </div>
                <hr className="m-3" />
                <div className="error-validations">
                  <span className="font-italic"> {manifestMessages.Mandatory}</span>
                  <div>
                    {Object.entries(validationErrors).map(([key, val]) => (
                      <li key={key}>{val}</li>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    ),
    [createResourceData?.manifestMetaData, validationErrors]
  );

  const popUpContent = () => (
    <ScrollView height="100%">
      <div>
        {errorMessage != null && (
          <div className="errorContainer">
            <FaTimesCircle
              className="errorCircle"
              onClick={() => setErrorMessage(null)}
            />
            <span>{errorMessage}</span>
          </div>
        )}
        <div>
          <span className="uploadFileClass">Upload file</span>
        </div>
        <div className={!createResourceData ? "createResForm" : ""}>
          <div className="fileuploader-container">
            <div className="widget-container flex-box">
              <div
                id="createresource-div-dropzone-external"
                className={!createResourceData ? "fileUploderOuterClass" : ""}
              >
                <div id="createresource-div-dropzone-text">
                  <div
                    className={
                      !createResourceData ? "fileUploader-text" : "fileUploader-text-post"
                    }
                  >
                    {!fileName && (
                      <span className="dropCerText">
                        {dropCertificateSVG}
                        <span className="uploadBtn">
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          Drop zip file here or click to upload
                        </span>
                      </span>
                    )}
                    {fileName && (
                      <div className="fileDisplayNameClass">
                        <div className="fileDisplayNameInnerClass ">
                          <div className="file-info">
                            <span className="file-zip-icon">{fileZipIconSVG}</span>

                            <span className="fileNameClass">{fileName}</span>
                          </div>

                          <div />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <FileUploader
                id="createresource-file-uploader"
                dialogTrigger="#createresource-div-dropzone-external"
                dropZone="#createresource-div-dropzone-external"
                labelText=""
                accept="zip"
                multiple={false}
                visible={false}
                disabled={fileName.length > 0}
                uploadMode="useButtons"
                onValueChange={handleFileUpload}
              />
            </div>
          </div>
        </div>
        {fileName && !isCheckingManifestDetails && (
          <Button
            className="removeZipClass"
            onClick={handleCancelUpload}
            stylingMode="text"
          >
            {removeZipSVG}
          </Button>
        )}
        {createResourceData && renderManifestValidatedTable()}
      </div>
      {isCheckingManifestDetails && !createResourceData && (
        <LoadPanel
          shadingColor="rgba(0,0,0,0.4)"
          visible={isCheckingManifestDetails}
          showIndicator={true}
          message="Validation in progress please wait"
        />
      )}
    </ScrollView>
  );
  return (
    <div className="create-resource-container">
      <Button onClick={togglePopupVisibility} stylingMode="text" className="btn-default">
        <div className="btn-icon">{addResourceSVG}</div>
        <div className="btn-text">Create resource</div>
      </Button>
      <div id="createresource-div-result">
        <PopUp
          dragEnabled={false}
          wrapperAttr={resContainerElement}
          visible={isCreatePopupVisible}
          hideOnOutsideClick={false}
          showTitle={true}
          title="Create resource"
          position="center"
          height="auto"
          width={800}
          contentRender={popUpContent}
          showCloseButton={false}
        >
          <ToolbarItem
            options={getCancelOptions()}
            widget="dxButton"
            location="after"
            toolbar="bottom"
            cssClass="cancel-resource-btn"
          />
          <ToolbarItem
            options={getCreateOptions()}
            widget="dxButton"
            location="after"
            disabled={validationErrors || !fileName}
            toolbar="bottom"
            cssClass="create-resource-btn"
          />
        </PopUp>
      </div>
    </div>
  );
};

CreateResource.propTypes = {
  refreshResourceGrid: PropTypes.func.isRequired,
};

export default CreateResource;
